import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { PHOTO_GAP_TIMEOUT_SECONDS } from '@transpordiamet/shared';
import { sessionAtom, lastPhotoTimestampAtom, remainingSecondsAtom } from '@/lib/atoms';

/**
 * Custom hook to monitor session timeout and redirect to restart page
 *
 * This hook:
 * - Runs a timer that updates remainingSecondsAtom every second
 * - Automatically redirects to restart page when timeout expires
 * - Prevents redirect if user is already on the restart page
 * - Only runs when there's at least one photo uploaded and session is active
 *
 * Should be called once at the SessionLayout level for global timeout monitoring
 */
export const useSessionTimeout = () => {
  const session = useAtomValue(sessionAtom);
  const lastPhotoTimestamp = useAtomValue(lastPhotoTimestampAtom);
  const setRemainingSeconds = useSetAtom(remainingSecondsAtom);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't run timer if:
    // - No last photo timestamp (no photos uploaded yet)
    // - Session is completed
    // - User is already on the restart page
    if (!lastPhotoTimestamp || session?.state === 'completed' || location.pathname.includes('/restart')) {
      setRemainingSeconds(null);
      return;
    }

    const interval = setInterval(() => {
      const expiryTime = dayjs(lastPhotoTimestamp).add(PHOTO_GAP_TIMEOUT_SECONDS, 'seconds');
      const remaining = expiryTime.diff(dayjs(), 'seconds');

      setRemainingSeconds(remaining);

      // Redirect to restart page if timeout expired and not already on restart page
      if (remaining <= 0 && !location.pathname.includes('/restart')) {
        navigate(`/${session?.id}/restart`);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      setRemainingSeconds(null);
    };
  }, [lastPhotoTimestamp, session?.state, session?.id, location.pathname, navigate, setRemainingSeconds]);
};
